import React, { useState } from 'react';
import {
  FaServer,
  FaReact,
  FaCloud,
  FaMobile,
  FaNetworkWired,
  FaDatabase,
  FaCode,
  FaChevronDown,
  FaChevronUp,
  FaMicrosoft
} from 'react-icons/fa';
import { SiSpringboot, SiDotnet, SiNodedotjs, SiAmazon, SiGooglecloud } from 'react-icons/si';

interface ServiceDetail {
  title: string;
  icon: JSX.Element;
  description: string;
  technologies: string[];
  capabilities: string[];
  useCases: string[];
}

const Services: React.FC = () => {
  const [expandedService, setExpandedService] = useState<number | null>(0);

  const services: ServiceDetail[] = [
    {
      title: 'Backend Development',
      icon: <FaServer className="text-4xl" />,
      description: 'We build scalable, secure backend systems designed for high-traffic, data-intensive, and mission-critical applications. Our backend services form the foundation of reliable digital products.',
      technologies: [
        'Java (Spring Boot, Spring Cloud)',
        '.NET (.NET Core, ASP.NET, Entity Framework)',
        'Golang (high-performance microservices)',
        'Node.js (NestJS, Express, Fastify)'
      ],
      capabilities: [
        'RESTful and GraphQL API development',
        'Microservices architecture and design',
        'Database design and optimization (SQL & NoSQL)',
        'Event-driven architecture with message queues',
        'Real-time data processing and streaming',
        'Authentication & authorization (OAuth2, JWT, SSO)',
        'Third-party API integration and middleware',
        'Performance optimization and caching strategies'
      ],
      useCases: [
        'Enterprise SaaS platforms',
        'FinTech and payment processing systems',
        'E-commerce backends with high transaction volumes',
        'Data-intensive analytics platforms',
        'Real-time collaboration tools'
      ]
    },
    {
      title: 'Frontend Development',
      icon: <FaReact className="text-4xl" />,
      description: 'We deliver responsive, maintainable frontend applications that prioritize user experience, performance, and long-term maintainability. Modern UI architecture that scales with your business.',
      technologies: [
        'React (Hooks, Context, Redux Toolkit)',
        'TypeScript for type-safe development',
        'Next.js for server-side rendering',
        'Modern CSS (Tailwind, CSS-in-JS)',
        'State management (Redux, Zustand, Recoil)'
      ],
      capabilities: [
        'Single Page Applications (SPA)',
        'Progressive Web Apps (PWA)',
        'Enterprise dashboards and admin panels',
        'Component library development',
        'Responsive and accessible UI design',
        'Performance optimization (lazy loading, code splitting)',
        'Cross-browser compatibility',
        'Integration with backend APIs and services'
      ],
      useCases: [
        'Customer-facing web applications',
        'Internal business tools and dashboards',
        'Data visualization platforms',
        'Content management systems',
        'Real-time monitoring interfaces'
      ]
    },
    {
      title: 'Cloud & Infrastructure',
      icon: <FaCloud className="text-4xl" />,
      description: 'We design and deploy cloud-native architectures with high availability, security, and cost efficiency in mind. From greenfield projects to cloud migration, we support the full lifecycle.',
      technologies: [
        'Amazon Web Services (AWS)',
        'Google Cloud Platform (GCP)',
        'Microsoft Azure',
        'Docker & Kubernetes',
        'Terraform & Infrastructure as Code'
      ],
      capabilities: [
        'Cloud architecture design and consulting',
        'Cloud migration from on-premise to cloud',
        'Serverless architecture (Lambda, Cloud Functions)',
        'Container orchestration with Kubernetes',
        'CI/CD pipeline setup (GitHub Actions, GitLab CI, Jenkins)',
        'Infrastructure as Code (Terraform, CloudFormation)',
        'Monitoring, logging, and observability (ELK, Prometheus, Grafana)',
        'Cost optimization and resource management'
      ],
      useCases: [
        'Legacy application cloud migration',
        'Auto-scaling high-traffic applications',
        'Multi-region deployments for global reach',
        'Disaster recovery and backup solutions',
        'DevOps process establishment'
      ]
    },
    {
      title: 'Microservices & Distributed Systems',
      icon: <FaNetworkWired className="text-4xl" />,
      description: 'Our engineers design microservice-based systems that scale independently, tolerate failures, and evolve without disrupting the business. Enterprise-grade architecture for complex domains.',
      technologies: [
        'Service mesh (Istio, Linkerd)',
        'Message brokers (RabbitMQ, Kafka, Redis)',
        'API Gateways (Kong, AWS API Gateway)',
        'Service discovery (Consul, Eureka)',
        'Distributed tracing (Jaeger, Zipkin)'
      ],
      capabilities: [
        'Microservices decomposition and design',
        'Domain-Driven Design (DDD) implementation',
        'Inter-service communication patterns',
        'Event sourcing and CQRS',
        'Circuit breaker and resilience patterns',
        'Distributed transaction management',
        'Service versioning and backward compatibility',
        'Performance monitoring and distributed tracing'
      ],
      useCases: [
        'Breaking monoliths into microservices',
        'Building scalable multi-tenant platforms',
        'Complex business domain modeling',
        'Systems requiring independent team scaling',
        'Applications with varying performance requirements'
      ]
    },
    {
      title: 'Mobile Development',
      icon: <FaMobile className="text-4xl" />,
      description: 'We build cross-platform mobile applications that share code across iOS and Android while maintaining native performance and UX standards. Deliver to both platforms faster and more efficiently.',
      technologies: [
        'React Native (iOS & Android)',
        'Ionic (Hybrid mobile apps)',
        'TypeScript for robust mobile code',
        'Native modules integration',
        'Mobile-specific state management'
      ],
      capabilities: [
        'Cross-platform mobile app development',
        'Native module integration when needed',
        'Offline-first architecture',
        'Push notifications and background tasks',
        'Mobile API integration and caching',
        'App store deployment (Apple App Store, Google Play)',
        'Mobile app performance optimization',
        'Biometric authentication and device features'
      ],
      useCases: [
        'Consumer mobile applications',
        'Enterprise mobile tools for field teams',
        'Mobile-first SaaS products',
        'E-commerce mobile apps',
        'Location-based services'
      ]
    }
  ];

  const toggleService = (index: number) => {
    setExpandedService(expandedService === index ? null : index);
  };

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive software development services covering the entire technology stack.
            We build production-ready solutions that power modern businesses.
          </p>
        </div>

        {/* Services Accordion */}
        <div className="space-y-4">
          {services.map((service, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              {/* Service Header */}
              <button
                onClick={() => toggleService(index)}
                className="w-full flex items-center justify-between p-6 bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-white transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 p-4 rounded-lg text-blue-600">
                    {service.icon}
                  </div>
                  <div className="text-left">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mt-1">
                      {service.description.split('.')[0]}
                    </p>
                  </div>
                </div>
                <div className="text-gray-400">
                  {expandedService === index ? (
                    <FaChevronUp className="text-2xl" />
                  ) : (
                    <FaChevronDown className="text-2xl" />
                  )}
                </div>
              </button>

              {/* Service Details */}
              {expandedService === index && (
                <div className="p-6 bg-white border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <FaCode className="mr-2 text-blue-600" />
                        Technologies & Frameworks
                      </h4>
                      <div className="space-y-2">
                        {service.technologies.map((tech, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{tech}</span>
                          </div>
                        ))}
                      </div>

                      <h4 className="text-lg font-bold text-gray-900 mb-4 mt-8 flex items-center">
                        <FaDatabase className="mr-2 text-blue-600" />
                        Use Cases
                      </h4>
                      <div className="space-y-2">
                        {service.useCases.map((useCase, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{useCase}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right Column */}
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-4">
                        Core Capabilities
                      </h4>
                      <div className="space-y-3">
                        {service.capabilities.map((capability, idx) => (
                          <div
                            key={idx}
                            className="flex items-start space-x-3 bg-gray-50 p-3 rounded-lg"
                          >
                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700 text-sm">{capability}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Technology Icons */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">
            Trusted Technologies We Master
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <SiSpringboot className="text-5xl text-green-600" title="Spring Boot" />
            <SiDotnet className="text-5xl text-purple-600" title=".NET" />
            <SiNodedotjs className="text-5xl text-green-500" title="Node.js" />
            <FaReact className="text-5xl text-blue-500" title="React" />
            <SiAmazon className="text-5xl text-orange-500" title="AWS" />
            <SiGooglecloud className="text-5xl text-blue-600" title="Google Cloud" />
            <FaMicrosoft className="text-5xl text-blue-600" title="Azure" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
